/**
 * UserClaimsService
 *
 * Manages user claims that are sent alongside the Firebase JWT token.
 * Since we cannot modify the JWT itself (it's cryptographically signed),
 * we send claims as a separate base64-encoded header that the backend
 * can read to avoid Firestore lookups on every request.
 *
 * The backend verifies the Firebase JWT (proves user identity via uid)
 * and reads claims from X-Loop-Claims header for user context (org, access_level, is_internal).
 */

import { getSession, setSession } from 'src/utils/functions/localStorage'
import { getAccessLevel } from 'src/utils/functions/accessLevel'

export interface UserClaims {
  org: string
  access_level: string
  is_internal: boolean
  timestamp: number // When claims were last updated
}

const CLAIMS_STORAGE_KEY = 'user_claims'
const CLAIMS_HEADER_KEY = 'X-Loop-Claims'

class UserClaimsService {
  private claims: UserClaims | null = null

  /**
   * Initialize claims from a user object
   * Called when user data is loaded/updated
   */
  setClaims(user: {
    org: string
    'access-level'?: string | string[]
    internal?: boolean
  }): void {
    if (!user?.org) {
      console.warn('UserClaimsService: Cannot set claims without org')
      return
    }

    this.claims = {
      org: user.org,
      access_level: getAccessLevel(user),
      is_internal: user.internal ?? false,
      timestamp: Date.now()
    }

    // Persist to session storage for page reloads
    setSession(CLAIMS_STORAGE_KEY, this.claims, true)
  }

  /**
   * Get the current claims
   */
  getClaims(): UserClaims | null {
    // Try memory first
    if (this.claims) {
      return this.claims
    }

    // Fall back to session storage
    const stored = getSession(CLAIMS_STORAGE_KEY)
    if (stored) {
      this.claims = stored
      return this.claims
    }

    return null
  }

  /**
   * Get claims as a base64-encoded string for the header
   */
  getClaimsHeader(): string | null {
    const claims = this.getClaims()
    if (!claims) {
      return null
    }

    try {
      const jsonString = JSON.stringify(claims)
      // Use btoa for base64 encoding (works in browser)
      return btoa(jsonString)
    } catch (err) {
      console.error('UserClaimsService: Failed to encode claims', err)
      return null
    }
  }

  /**
   * Get the header key name
   */
  getHeaderKey(): string {
    return CLAIMS_HEADER_KEY
  }

  /**
   * Clear claims (on logout)
   */
  clearClaims(): void {
    this.claims = null
    try {
      sessionStorage.removeItem(CLAIMS_STORAGE_KEY)
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Check if claims need to be updated based on user data changes
   */
  needsUpdate(user: {
    org: string
    'access-level'?: string | string[]
    internal?: boolean
  }): boolean {
    const current = this.getClaims()
    if (!current) return true

    return (
      current.org !== user.org ||
      current.access_level !== getAccessLevel(user) ||
      current.is_internal !== (user.internal ?? false)
    )
  }
}

// Export singleton instance
export const userClaimsService = new UserClaimsService()
